import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { ProjectRepo } from "@/lib/types";

export function useProjectRepos() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["project-repos", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("project_repos")
        .select("*")
        .eq("user_id", user.id)
        .order("project_slug");

      if (error) throw error;
      return data as ProjectRepo[];
    },
    enabled: !!user,
  });
}

export function useUpsertProjectRepo() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (repo: { project_slug: string; repo_path: string; github_repo: string; default_branch: string }) => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("project_repos")
        .upsert(
          { ...repo, user_id: user.id },
          { onConflict: "user_id,project_slug" }
        )
        .select()
        .single();

      if (error) throw error;
      return data as ProjectRepo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-repos"] });
    },
  });
}

export function useDeleteProjectRepo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_repos")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-repos"] });
    },
  });
}
